import { FormattedInputType } from '../inputs';
import { RecurrenceFreq } from '../types';
import { sortBy } from '../util/arrays';
import { typedKeys } from '../util/objects';
import { BaseConfig, feBaseConfigs } from './baseConfig';
import { retirementAccountSubtypeLabelMap } from './helpers';
import {
    ExternalFinancialEntitiesMap,
    ExternalFinancialEntityType,
    FinancialCategory,
} from './types';

export interface FieldConfig {
    label: string;
    name: string;
    type:
        | Exclude<FormattedInputType, 'email' | 'phone' | 'zipCode'>
        | 'select'
        | 'radioCards'
        // N.B. checkbox and transactionSchedulePlan are ONLY supported on web
        | 'checkbox'
        | 'transactionSchedulePlan';
    options?: { value: string; label: string }[];
    inputOptions?: { min?: number };
    defaultValue?: any;
    isRequired?: boolean;
    isVisible?: (form: Record<string, unknown>) => boolean;
    // N.B. The options below are ONLY supported on web
    transactionScheduleInputOptions?: {
        fixedFreq?: RecurrenceFreq;
    };
    showPlusOrMinusComponent?: boolean;
    rightText?: string;
}

export interface FinancialEntityUIConfig {
    addForm: {
        fields: FieldConfig[];
    };
    detailView: {
        primaryField: FieldConfig;
        expectedFields: FieldConfig[];
        otherFields: FieldConfig[];
        infoSectionTitle: string;
    };
    balancesView: {
        field: FieldConfig;
    };
    snapshotFieldNames: string[];
}

export interface FinancialEntityConfig
    extends FinancialEntityUIConfig,
        BaseConfig {}

const nameField = ({
    label = 'Name',
    isRequired,
}: { label?: string; isRequired?: boolean } = {}): FieldConfig => ({
    name: 'name',
    label,
    type: 'text',
    isRequired,
});
const amountField: FieldConfig = {
    name: 'balance',
    label: 'Amount',
    type: 'currency',
    isRequired: true,
};
const balanceField: FieldConfig = {
    name: 'balance',
    label: 'Balance',
    type: 'currency',
    isRequired: true,
    inputOptions: {
        min: 0,
    },
};
const interestRateField: FieldConfig = {
    name: 'interestRate',
    label: 'Interest rate',
    type: 'percent',
    isRequired: false,
    inputOptions: {
        min: 0,
    },
};
const valueField: FieldConfig = {
    name: 'balance',
    label: 'Current value',
    type: 'currency',
    isRequired: true,
    inputOptions: {
        min: 0,
    },
};
const expectedReturnField: FieldConfig = {
    name: 'expectedReturn',
    label: 'Expected annual return',
    type: 'percent',
    defaultValue: 0,
};
const minimumPaymentField: FieldConfig = {
    name: 'minimumPayment',
    label: 'Minimum monthly payment',
    type: 'currency',
    isRequired: true,
    inputOptions: {
        min: 0,
    },
};
const currentPaymentField: FieldConfig = {
    name: 'currentPayment',
    label: 'Current monthly payment',
    type: 'currency',
    inputOptions: {
        min: 0,
    },
};
const retirementAccountSubtypeField: FieldConfig = {
    name: 'subtype',
    label: 'Account type',
    type: 'select',
    options: sortBy(
        typedKeys(retirementAccountSubtypeLabelMap).map((subtype) => ({
            label: retirementAccountSubtypeLabelMap[subtype],
            value: subtype,
        })),
        'label',
        'ASC'
    ),
    isRequired: true,
};
const propertyTypeField: FieldConfig = {
    name: 'subtype',
    label: 'Property type',
    type: 'select',
    options: [
        { value: 'PRIMARY', label: 'Primary' },
        { value: 'RENTAL', label: 'Rental' },
        { value: 'VACATION', label: 'Vacation' },
    ],
    defaultValue: 'PRIMARY',
    isRequired: true,
};
const otherAssetTypeField: FieldConfig = {
    name: 'subtype',
    label: 'Type',
    type: 'select',
    options: [
        { value: 'COLLECTIBLES', label: 'Collectibles' },
        { value: 'ART', label: 'Art' },
        { value: 'JEWELRY', label: 'Jewelry' },
        { value: 'EBT', label: 'Electronic Benefit Transfer' },
        { value: 'GIC', label: 'Guaranteed Investment Certificate' },
        { value: 'UGMA', label: 'Uniform Gift to Minors Act' },
        { value: 'UTMA', label: 'Uniform Transfer to Minors Act' },
        { value: 'INSURANCE', label: 'Insurance' },
        { value: 'LIFE_INSURANCE', label: 'Life Insurance' },
        { value: 'PROFIT_SHARING_PLAN', label: 'Profit Sharing Plan' },
        { value: 'OTHER', label: 'Other' },
    ],
    defaultValue: 'COLLECTIBLES',
    isRequired: true,
};
const otherInvestmentTypeField: FieldConfig = {
    name: 'subtype',
    label: 'Type',
    type: 'select',
    options: [
        { value: 'OTHER', label: 'Other' },
        { value: 'CRYPTO', label: 'Crypto' },
        { value: 'ESPP', label: 'Employee Stock Purchase Plan' },
        { value: 'ESA', label: 'Education Savings Account' },
        { value: 'RSU', label: 'Restricted Stock Units' },
        { value: 'TAX_DEFERRED', label: 'Tax Deferred Investment' },
        { value: 'TRUST', label: 'Trust' },
        { value: 'ANNUITY', label: 'Annuity' },
    ],
    defaultValue: 'OTHER',
    isRequired: true,
};
const purchaseAmountField: FieldConfig = {
    name: 'purchaseAmount',
    label: 'Purchase price',
    type: 'currency',
    isRequired: false,
    inputOptions: {
        min: 0,
    },
};

const expectedContributionField = (label: string): FieldConfig => ({
    name: 'expectedContribution',
    label: `Expected ${label}`,
    type: 'transactionSchedulePlan',
    showPlusOrMinusComponent: true,
    defaultValue: {
        amount: 0,
        recurrence: { freq: 'MONTHLY', interval: 1 },
    },
});

const expectedChangeField: FieldConfig = {
    name: 'expectedChange',
    label: 'Expected change',
    type: 'transactionSchedulePlan',
    showPlusOrMinusComponent: true,
    defaultValue: {
        amount: 0,
        recurrence: { freq: 'MONTHLY', interval: 1 },
    },
};
const plannedPaymentField: FieldConfig = {
    name: 'plannedPayment',
    label: 'Planned payment',
    type: 'transactionSchedulePlan',
    transactionScheduleInputOptions: {
        fixedFreq: 'MONTHLY',
    },
    inputOptions: {
        min: 0,
    },
    defaultValue: {
        amount: 0,
        recurrence: { freq: 'MONTHLY', interval: 1 },
    },
};

const carDetailFields: FieldConfig[] = [
    {
        name: 'make',
        label: 'Make',
        type: 'text',
        isRequired: true,
    },
    {
        name: 'model',
        label: 'Model',
        type: 'text',
        isRequired: true,
    },
    {
        name: 'year',
        label: 'Year',
        type: 'integer',
        isRequired: true,
    },
];

// Maps ui-specific financial entity configs to their typenames.
// These are zipped together with financial entities' base configs, below,
// and exported for use as one comprehensive config for each financial entity.

const feUIConfigs: ExternalFinancialEntitiesMap<FinancialEntityUIConfig> =
    Object.freeze({
        CashHolding: {
            addForm: {
                fields: [amountField],
            },
            detailView: {
                primaryField: amountField,
                expectedFields: [],
                otherFields: [],
                infoSectionTitle: 'Cash details',
            },
            balancesView: {
                field: amountField,
            },
            snapshotFieldNames: ['balance'],
        },
        CheckingAccount: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of checking account' }),
                    balanceField,
                    interestRateField,
                ],
            },
            detailView: {
                primaryField: balanceField,
                expectedFields: [],
                otherFields: [interestRateField],
                infoSectionTitle: 'Account details',
            },
            balancesView: {
                field: balanceField,
            },
            snapshotFieldNames: ['balance'],
        },
        SavingsAccount: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of savings account' }),
                    balanceField,
                    interestRateField,
                ],
            },
            detailView: {
                primaryField: balanceField,
                expectedFields: [expectedChangeField],
                otherFields: [interestRateField],
                infoSectionTitle: 'Account details',
            },
            balancesView: {
                field: balanceField,
            },
            snapshotFieldNames: ['balance'],
        },
        CD: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of CD' }),
                    balanceField,
                    interestRateField,
                ],
            },
            detailView: {
                primaryField: balanceField,
                expectedFields: [expectedChangeField],
                otherFields: [interestRateField],
                infoSectionTitle: 'Account details',
            },
            balancesView: {
                field: balanceField,
            },
            snapshotFieldNames: ['balance'],
        },
        MoneyMarketAccount: {
            addForm: {
                fields: [
                    // N.B. "Name of money market account" doesn't fit on one line
                    nameField({ label: 'Name of account' }),
                    balanceField,
                    interestRateField,
                ],
            },
            detailView: {
                primaryField: balanceField,
                expectedFields: [expectedChangeField],
                otherFields: [interestRateField],
                infoSectionTitle: 'Account details',
            },
            balancesView: {
                field: balanceField,
            },
            snapshotFieldNames: ['balance'],
        },
        BrokerageAccount: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of account' }),
                    valueField,
                    expectedReturnField,
                ],
            },
            detailView: {
                primaryField: valueField,
                expectedFields: [expectedContributionField('investment')],
                otherFields: [expectedReturnField],
                infoSectionTitle: 'Account details',
            },
            balancesView: {
                field: valueField,
            },
            snapshotFieldNames: ['balance', 'investedAmount', 'returnValue'],
        },
        OtherInvestment: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of account' }),
                    valueField,
                    expectedReturnField,
                    otherInvestmentTypeField,
                ],
            },
            detailView: {
                primaryField: valueField,
                expectedFields: [expectedContributionField('investment')],
                otherFields: [expectedReturnField, otherInvestmentTypeField],
                infoSectionTitle: 'Account details',
            },
            balancesView: {
                field: valueField,
            },
            snapshotFieldNames: ['balance', 'investedAmount', 'returnValue'],
        },
        RetirementAccount: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of account' }),
                    valueField,
                    retirementAccountSubtypeField,
                    expectedReturnField,
                ],
            },
            detailView: {
                primaryField: valueField,
                expectedFields: [expectedContributionField('contribution')],
                otherFields: [expectedReturnField],
                infoSectionTitle: 'Account details',
            },
            balancesView: {
                field: valueField,
            },
            snapshotFieldNames: [
                'balance',
                'investedAmount',
                'returnValue',
                'contributedAmount',
                'contributedPercentOfLimit',
            ],
        },
        S529Account: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of 529c account' }),
                    valueField,
                    expectedReturnField,
                ],
            },
            detailView: {
                primaryField: valueField,
                expectedFields: [expectedContributionField('contribution')],
                otherFields: [expectedReturnField],
                infoSectionTitle: 'Account details',
            },
            balancesView: {
                field: valueField,
            },
            snapshotFieldNames: ['balance', 'investedAmount', 'returnValue'],
        },
        HSA: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of HSA account' }),
                    valueField,
                    expectedReturnField,
                ],
            },
            detailView: {
                primaryField: valueField,
                expectedFields: [expectedContributionField('investment')],
                otherFields: [expectedReturnField],
                infoSectionTitle: 'Account details',
            },
            balancesView: {
                field: valueField,
            },
            snapshotFieldNames: ['balance', 'investedAmount', 'returnValue'],
        },
        StockPlan: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of stock plan' }),
                    valueField,
                    expectedReturnField,
                ],
            },
            detailView: {
                primaryField: valueField,
                expectedFields: [expectedContributionField('investment')],
                otherFields: [expectedReturnField],
                infoSectionTitle: 'Account details',
            },
            balancesView: {
                field: valueField,
            },
            snapshotFieldNames: ['balance', 'investedAmount', 'returnValue'],
        },
        RealEstateProperty: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of property' }),
                    valueField,
                    expectedReturnField,
                    propertyTypeField,
                ],
            },
            detailView: {
                primaryField: valueField,
                expectedFields: [
                    {
                        ...expectedReturnField,
                        label: 'Expected appreciation',
                        rightText: '/ year',
                    },
                ],
                otherFields: [propertyTypeField],
                infoSectionTitle: 'Property details',
            },
            balancesView: {
                field: valueField,
            },
            snapshotFieldNames: ['balance'],
        },
        Car: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of car' }),
                    valueField,
                    ...carDetailFields,
                ],
            },
            detailView: {
                primaryField: valueField,
                expectedFields: [
                    {
                        ...expectedReturnField,
                        label: 'Expected change in value',
                        showPlusOrMinusComponent: true,
                        rightText: '/ year',
                    },
                ],
                otherFields: carDetailFields,
                infoSectionTitle: 'Car details',
            },
            balancesView: {
                field: valueField,
            },
            snapshotFieldNames: ['balance'],
        },
        OtherAsset: {
            addForm: {
                fields: [
                    nameField({ label: 'Name', isRequired: true }),
                    valueField,
                    purchaseAmountField,
                    { ...expectedReturnField, defaultValue: 0 },
                    otherAssetTypeField,
                ],
            },
            detailView: {
                primaryField: valueField,
                expectedFields: [
                    {
                        ...expectedReturnField,
                        label: 'Expected change in value',
                        showPlusOrMinusComponent: true,
                        rightText: '/ year',
                    },
                ],
                otherFields: [
                    purchaseAmountField,
                    expectedReturnField,
                    otherAssetTypeField,
                ],
                infoSectionTitle: 'Asset details',
            },
            balancesView: {
                field: valueField,
            },
            snapshotFieldNames: ['balance'],
        },
        CreditCard: {
            addForm: {
                fields: [
                    nameField({
                        label: 'Name of credit card',
                        isRequired: true,
                    }),
                    {
                        name: 'isPaidOffMonthly',
                        label: 'Paid fully monthly?',
                        type: 'radioCards',
                        options: [
                            { value: 'true', label: 'Yes' },
                            { value: 'false', label: 'No' },
                        ],
                        isRequired: true,
                    },
                    {
                        ...balanceField,
                        isVisible: (form) => form.isPaidOffMonthly === 'false',
                    },
                    {
                        ...interestRateField,
                        isVisible: (form) => form.isPaidOffMonthly === 'false',
                    },
                    {
                        ...minimumPaymentField,
                        isVisible: (form) => form.isPaidOffMonthly === 'false',
                    },
                    {
                        ...currentPaymentField,
                        isVisible: (form) => form.isPaidOffMonthly === 'false',
                    },
                ],
            },
            detailView: {
                primaryField: balanceField,
                expectedFields: [
                    plannedPaymentField,
                    {
                        name: 'isPaidOffMonthly',
                        label: 'I plan on paying off the full balance of my card every month',
                        type: 'checkbox',
                    },
                ],
                otherFields: [
                    interestRateField,
                    minimumPaymentField,
                    currentPaymentField,
                ],
                infoSectionTitle: 'Credit card details',
            },
            balancesView: {
                field: balanceField,
            },
            snapshotFieldNames: ['balance'],
        },
        HomeMortgage: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of home mortgage' }),
                    balanceField,
                    interestRateField,
                    minimumPaymentField,
                    currentPaymentField,
                ],
            },
            detailView: {
                primaryField: balanceField,
                expectedFields: [plannedPaymentField],
                otherFields: [
                    interestRateField,
                    minimumPaymentField,
                    currentPaymentField,
                ],
                infoSectionTitle: 'Mortgage details',
            },
            balancesView: {
                field: balanceField,
            },
            snapshotFieldNames: ['balance'],
        },
        CarLoan: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of car loan' }),
                    balanceField,
                    interestRateField,
                    minimumPaymentField,
                    currentPaymentField,
                ],
            },
            detailView: {
                primaryField: balanceField,
                expectedFields: [plannedPaymentField],
                otherFields: [
                    interestRateField,
                    minimumPaymentField,
                    currentPaymentField,
                ],
                infoSectionTitle: 'Loan details',
            },
            balancesView: {
                field: balanceField,
            },
            snapshotFieldNames: ['balance'],
        },
        StudentLoan: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of student loan' }),
                    balanceField,
                    interestRateField,
                    minimumPaymentField,
                    currentPaymentField,
                ],
            },
            detailView: {
                primaryField: balanceField,
                expectedFields: [plannedPaymentField],
                otherFields: [
                    interestRateField,
                    minimumPaymentField,
                    currentPaymentField,
                ],
                infoSectionTitle: 'Loan details',
            },
            balancesView: {
                field: balanceField,
            },
            snapshotFieldNames: ['balance'],
        },
        LineOfCredit: {
            addForm: {
                fields: [
                    nameField({
                        label: 'Name of line of credit',
                        isRequired: true,
                    }),
                    balanceField,
                    interestRateField,
                    minimumPaymentField,
                    currentPaymentField,
                ],
            },
            detailView: {
                primaryField: balanceField,
                expectedFields: [plannedPaymentField],
                otherFields: [
                    interestRateField,
                    minimumPaymentField,
                    currentPaymentField,
                ],
                infoSectionTitle: 'Line of credit details',
            },
            balancesView: {
                field: balanceField,
            },
            snapshotFieldNames: ['balance'],
        },
        OtherLiability: {
            addForm: {
                fields: [
                    nameField({ label: 'Name of debt', isRequired: true }),
                    balanceField,
                    interestRateField,
                    minimumPaymentField,
                    currentPaymentField,
                ],
            },
            detailView: {
                primaryField: balanceField,
                expectedFields: [plannedPaymentField],
                otherFields: [
                    interestRateField,
                    minimumPaymentField,
                    currentPaymentField,
                ],
                infoSectionTitle: 'Debt details',
            },
            balancesView: {
                field: balanceField,
            },
            snapshotFieldNames: ['balance'],
        },
    });

// Zips together and exports ui + base configs for each financial entity
export const feConfigs = Object.freeze(
    typedKeys(feUIConfigs).reduce((configs, typename) => {
        const baseConfig = feBaseConfigs[typename];
        const uiConfig = feUIConfigs[typename];
        configs[typename] = {
            ...baseConfig,
            ...uiConfig,
        };
        return configs;
    }, {} as ExternalFinancialEntitiesMap<FinancialEntityConfig>)
);

type CategoryConfig = { types: ExternalFinancialEntityType[] };
type CategoryConfigs = Record<FinancialCategory | 'All', CategoryConfig>;

const makeCategoryConfigs = (): CategoryConfigs => {
    const configs: CategoryConfigs = {
        CashAccount: { types: [] },
        Investment: { types: [] },
        Retirement: { types: [] },
        OtherAsset: { types: [] },
        Debt: { types: [] },
        CreditCard: { types: [] },
        All: { types: [] },
    };

    // Build category typename arrays
    typedKeys(feBaseConfigs).forEach((t) =>
        configs[feBaseConfigs[t].category].types.push(t)
    );

    // Build "All" category typename array from other categories
    configs.All.types = [
        ...configs.CashAccount.types,
        ...configs.Investment.types,
        ...configs.Retirement.types,
        ...configs.OtherAsset.types,
        ...configs.Debt.types,
        ...configs.CreditCard.types,
    ];

    return configs;
};

export const financialCategoryConfigs = makeCategoryConfigs();
